import { apiRequest } from "@/data/api/client";
import {
  payloadToActivity,
  payloadToSubcategories,
  type ActivityPayload,
  type TaxonomyPayload,
} from "@/data/mappers/partnerActivity.mapper";
import type { AuthorableBookingMode } from "@/domain/activity/rules";
import type { Activity, ActivitySubcategory, SaleEndAnchor } from "@/domain/activity/types";
import type { CustomFieldDefinition } from "@/domain/custom-fields/types";
import { toMinorUnits } from "@/data/mappers/money.mapper";

export interface ActivityDraft {
  name: string;
  subcategoryId: number;
  priceOriginal: number | null;
  reservationUrl: string | null;
  bookingMode: AuthorableBookingMode;
  description: string | null;
  imageUrl: string | null;
  /** Null asks the customer nothing. */
  customFields: CustomFieldDefinition[] | null;
  /** Copied onto every slevový termín published from here; null asks the
   *  partner for it each time (ADR 0007). Koruny — the API is told haléře. */
  defaultPriceDiscounted: number | null;
  defaultCapacity: number | null;
  saleLeadTimeHours: number | null;
  saleEndOffsetMinutes: number | null;
  saleEndAnchor: SaleEndAnchor | null;
  defaultDurationMinutes: number | null;
  requiresConfirmation: boolean;
}

/** An omitted property leaves the stored one alone — which is how an edit keeps
 *  the booking link partners no longer author. */
export type ActivityPatch = Partial<ActivityDraft>;

/** An absent price stays absent, so a patch that never mentioned it still leaves
 *  the stored default alone. */
function toActivityBody(patch: ActivityPatch): Record<string, unknown> {
  const { defaultPriceDiscounted, ...rest } = patch;
  if (defaultPriceDiscounted === undefined) return rest;
  return { ...rest, defaultPriceDiscountedMinorUnits: toMinorUnits(defaultPriceDiscounted) };
}

export const partnerActivitiesRepo = {
  /** The API answers in `activity_id` order; the partner reads their newest
   *  activity first, so the list is turned around here. */
  async list(): Promise<Activity[]> {
    const payloads = await apiRequest<ActivityPayload[]>("/v1/partner/activities");
    return payloads.map(payloadToActivity).sort((a, b) => b.id - a.id);
  },

  async listSubcategories(): Promise<ActivitySubcategory[]> {
    return payloadToSubcategories(await apiRequest<TaxonomyPayload>("/v1/partner/taxonomy"));
  },

  /** Answers with an id rather than the activity, so the caller refetches
   *  instead of trusting what it sent back. */
  async create(draft: ActivityDraft): Promise<number> {
    const { id } = await apiRequest<{ id: number }>("/v1/partner/activities", {
      method: "POST",
      body: toActivityBody(draft),
    });
    return id;
  },

  async update(activityId: number, patch: ActivityPatch): Promise<void> {
    await apiRequest(`/v1/partner/activities/${activityId}`, { method: "PATCH", body: toActivityBody(patch) });
  },

  async remove(activityId: number): Promise<void> {
    await apiRequest(`/v1/partner/activities/${activityId}`, { method: "DELETE" });
  },
};
